// @ts-strict-ignore
import { EventDispatcher, Vector3 } from 'three';
import { ProjectModel } from '../../../model/project.model';
import { SelectionModel } from '../model/selection.model';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { MountingRailHelper } from '../../../helper/mounting-rail.helper';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Dictionary } from 'typescript-collections';
import { StringUtils } from '@shared/util/string-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { RangeOnLineArticleData } from '../model/range-on-line-article-data';
import { IOperation } from '@shared/undo/operation/i-operation';
import { UndoAttachArticlesOperation } from '@shared/undo/operation/undo-attach-articles-operation';
import { BaseAdvancedOperation } from '@shared/undo/operation/base-advanced-operation';
import { UndoDetachArticlesOperation } from '@shared/undo/operation/undo-detach-articles-operation';
import { UndoInsertArticlesByLocationOperation } from '@shared/undo/operation/undo-insert-articles-by-location-operation';
import { UndoReplaceArticlesByLocationOperation } from '@shared/undo/operation/undo-replace-articles-by-location-operation';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { IBaseArticleProjectProcessor } from '@article/model/i-base-article-project.processor';
import { BaseArticleProjectProcessorFactory } from '@article/model/base-article-project-processor-factory';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { ArticleManager } from '@article/manager/article.manager';
import { OpenLabelEditorHelper } from '@project/components/label/label-editor/service/open-label-editor.helper';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { lastValueFrom, Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { InsertArticleUtils } from '../util/insert-article-utils';
import { ProjectChangedType } from '../../../model/project-changed';
import { Log } from '@shared/debug/log';
import { InsertedArticleData } from '../processor/i-article-location.processor';
import { InsertArticleService } from '../service/insert-article.service';
import { filter } from 'rxjs/operators';
import { SharedComponentsPresenter } from '@shared/shared-components.presenter';
import { SelectionModelService } from '../service/selection-model.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ProServeProjectImportProcessor } from '../../import-export/processor/pro-serve-project-import.processor';
import { AddArticleError } from '../processor/add-article-error';
import { ProjectType } from '../../../model/project-type';
import { UserModel } from '@user/model/user.model';

@Injectable({ providedIn: 'root' })
export class ProjectArticleManager extends EventDispatcher {
  public replaceAvailable: boolean = false;
  public replaceAvailableChange: BehaviorSubject<boolean>;
  public multipleInsertAvailable: boolean = false;

  private _baseArticleProjectProcessor: IBaseArticleProjectProcessor;

  private _selectedArticle: ArticleVO;
  private _articleCount: number = 0;
  private _processingArticleCount: number = 0;

  private _processingProjectData: any = null;
  private _projectDatas: RangeOnLineArticleData[] = [];
  private _detachedArticleGUIDs: string[] = [];
  private _insertedArticleGUIDs: string[] = [];
  private _moveArticlesMap: Dictionary<string, Vector3> = null;
  private _createdUndoOperations: IOperation[] = null;
  private _projectDataCompletelyProcessed: boolean = false;
  private _failureMessage: string = null;
  private _executing: boolean = false;

  private _addArticleAlertActive: boolean = false;

  constructor(
    private readonly alertHelper: AlertHelper,
    private readonly projectModel: ProjectModel,
    private readonly detachArticleUsageService: DetachArticleUsageService,
    public readonly selectionModel: SelectionModel,
    public readonly selectionModelService: SelectionModelService,
    public readonly articleManager: ArticleManager,
    public readonly undoRedoHelper: UndoRedoHelper,
    public readonly openLabelEditorHelper: OpenLabelEditorHelper,
    public readonly mountingRailHelper: MountingRailHelper,
    private readonly localeResourceManager: LocaleResourceManager,
    private readonly proServeProjectImportProcessor: ProServeProjectImportProcessor,
    private readonly baseArticleProjectProcessorFactory: BaseArticleProjectProcessorFactory,
    private readonly containerService: ContainerService,
    private readonly undoRedoUtils: UndoRedoUtils,
    private readonly insertArticleUtils: InsertArticleUtils,
    private readonly _insertArticleService: InsertArticleService,
    private readonly sharedComponentsPresenter: SharedComponentsPresenter,
    private readonly userModel: UserModel,
  ) {
    super();
    this.replaceAvailableChange = new BehaviorSubject<boolean>(this.replaceAvailable);
    this.subscribeProjectChanged();
    this.subscribeOnArticleInserted();
    this.subscribeDeselected();
  }

  /** Adds the requested article n times to the configuration
   * @returns false if the operation failed or was aborted by the user. */
  public async addArticles(article: ArticleVO, articleCount: number): Promise<boolean> {
    if (!this._baseArticleProjectProcessor || (this._executing && !this.multipleInsertAvailable) || !article) {
      return false;
    }

    if (await this.shouldNotAddOutdatedArticle(article)) {
      return false;
    }

    return this.continueAddArticles(article, articleCount);
  }

  private async shouldNotAddOutdatedArticle(article: ArticleVO): Promise<boolean> {
    if (!ArticleUtils.isArticleOutdated(article)) {
      return false;
    }

    const title: string = this.localeResourceManager.getValueByKey('outdatedArticlesAlert.title');
    const message: string = ArticleUtils.getOutdatedArticleLabelWithContinue(article, this.localeResourceManager);

    const answer = await this.alertHelper.showAlert(title, message, undefined, [AlertButton.YES, AlertButton.NO], "error");
    return answer === AlertButton.NO;
  }

  protected subscribeOnArticleInserted(): void {
    this._insertArticleService.articleInserted$
      .pipe(filter((data) => data.messageType === 'replaceArticleOnMountingRail' || data.messageType === 'alternativeReplaceArticleOnMountingRail'))
      .subscribe({
        next: (data) => {
          if (data.error) {
            this.addArticle_failure(data.error.message);
          }
        },
      });
  }

  private async continueAddArticles(article: ArticleVO, articleCount: number): Promise<boolean> {
    if (this._articleCount === 0) {
      this.clear();
      this._executing = true;
      this._articleCount = articleCount;
      this._processingArticleCount = articleCount;
      this._selectedArticle = article;

      // load full article (instead of given article-head) to be able to prepare project-data correctly
      const fullArticle: ArticleVO = this.articleManager.getCachedArticle(article.identity, article.version);
      if (!fullArticle) {
        try {
          const newArticle: ArticleVO = await lastValueFrom(this.articleManager.newLoadArticle_handler(article.identity, article.version));
          return await this.startAddingArticlesByProjectData(newArticle);
        } catch (error) {
          Log.error('Could not load full article: ', error);
          await this.loadArticle_failure();
        }
      } else {
        return await this.startAddingArticlesByProjectData(fullArticle);
      }
    } else if (article.identity === this._selectedArticle.identity && article.version === this._selectedArticle.version) {
      this._processingArticleCount += articleCount;
    }
    return false;
  }

  private async loadArticle_failure(): Promise<void> {
    const errorMessage: string = this.localeResourceManager.getValueByKey('articleLocationProcessor.message.error_load_article', [
      this._selectedArticle.articleNo,
    ]);
    await this.addArticleProcess_failure(errorMessage);
  }

  private async startAddingArticlesByProjectData(article: ArticleVO): Promise<boolean> {
    this._selectedArticle = article;
    this._projectDatas = this._baseArticleProjectProcessor.prepareProjectDataBySelection(this._selectedArticle);
    if (this._articleCount > 0) {
      return await this.addArticleByNextProjectData();
    }
    return false;
  }

  public async addArticlesOnLeftSide(article: ArticleVO, articleCount: number): Promise<void> {
    if (!this._baseArticleProjectProcessor || (this._executing && !this.multipleInsertAvailable) || !article) {
      return;
    }
    if (this._articleCount === 0) {
      this.clear();
      this._executing = true;
      this._articleCount = articleCount;
      this._processingArticleCount = articleCount;
      this._selectedArticle = article;
      this._projectDatas = this.mountingRailHelper.createRangeOnLineArticleDataBySelection(null, false);
      if (articleCount > 0) {
        this._processingProjectData = this._projectDatas[0];
        const neigbour: ArticleContainer = (this._processingProjectData as RangeOnLineArticleData).lastArticleBeforeSelection;
        if (neigbour) {
          await this.addArticleByNeighbor(neigbour.articleUsage.id);
        } else {
          try {
            const data = await this._baseArticleProjectProcessor.insertArticleLeftOfSelectedArticles(article, this._processingProjectData);
            await this.addArticleProcess_success(data);
          } catch (error) {
            Log.error('Could not insert article on left side: ', error);
            await this.addArticleProcess_failure(error?.error);
          }
        }
      }
    } else if (article.identity === this._selectedArticle.identity && article.version === this._selectedArticle.version) {
      this._processingArticleCount += articleCount;
    }
  }

  protected subscribeProjectChanged() {
    this.projectModel.projectChanged$
      .pipe(filter(change => change.type === ProjectChangedType.ProjectTypeChanged))
      .subscribe((projectChanged) => this.projectTypeChanged(projectChanged.projectHeadVO.projectType));
  }

  private projectTypeChanged(projectType: ProjectType): void {
    if (this._baseArticleProjectProcessor) {
      this._baseArticleProjectProcessor = null;
    }

    if (!projectType) {
      return;
    }
    this._baseArticleProjectProcessor = this.baseArticleProjectProcessorFactory.createBaseArticleManagerByProjectType(
      projectType,
      'addArticleProcess'
    );
    this.multipleInsertAvailable = this.isMultipleInsertAvailable();
  }

  public articleSelectionChanged(): void {
    this.replaceAvailable = this.selectionModel.isArticleSelection() && this.isReplaceOperationAvailable();
    this.replaceAvailableChange.next(this.replaceAvailable);
  }

  private async addArticleProcess_success(data: InsertedArticleData): Promise<boolean> {
    // at the moment baseGuid is not specified in success-message only in case when added articles are accessories
    // added/connected accessory articles should not be selected after connect-operation
    // so this checking is used to determine the case when selection of newly added articles is needed

    const baseGuid: string = data.baseArticleGUID;
    const addedArticleGUIDs: string[] = data.insertedArticleGUIDs;
    const moveMap: Dictionary<string, Vector3> = data.moveArticlesMap;
    const createUndoOperation: boolean = data.createUndoOperation;
    const additionalUndoOperations: IOperation[] = data.undoOperations;

    if (!StringUtils.isEmptyOrLiteralNull(baseGuid)) {
      this.selectionModelService.selectArticlesByUsageIds(addedArticleGUIDs);
    }

    if (addedArticleGUIDs != null && createUndoOperation) {
      this._insertedArticleGUIDs = this._insertedArticleGUIDs.concat(addedArticleGUIDs);
      if (this._baseArticleProjectProcessor) {
        ArrayUtils.mergeArrays(this._detachedArticleGUIDs, this._baseArticleProjectProcessor.getArticleUsageIdsToDetach());
      }
      this.mergeMoveArticlesMap(moveMap);
      this.concatCreatedUndoOperations(additionalUndoOperations);
    }
    // the delayed execution is here, because there are two (related) problems, which are new, because not all models need
    // to be parsed again by ModelParser, but are instead instantly available by cloning.
    // So the execution is not any longer broken up by the delayed parsing inherit in ModelParser:
    //
    // 1. Because the execution is not broken up, there is a direct call-loop (created by directly executed events in parsely framework)
    //   right back to this event-handler function "addArticleProcess_success", so through a long call-stack-hierarchy the call of
    //   addArticleByNeighbor() leads directly to the next call of addArticleProcess_success. This way a very long call stack is possible
    //  (adding the same article 50x times), which leads to a call stack which exceeds the max length, and therefore causing an exception.
    //
    // 2. Because executing is not broken up, there is not time to enter new frame and therefore triggering a re-render.
    // So the user doesnt see the progression of the added articles.
    if (--this._processingArticleCount > 0 && !StringUtils.isEmptyOrLiteralNull(baseGuid)) {
      await this.addArticleByNeighbor(baseGuid);
      return true;
    } else {
      this._projectDataCompletelyProcessed = true;
      return await this.addArticleByNextProjectData();
    }
  }

  private async addArticleProcess_failure(message: string): Promise<boolean> {
    this._processingArticleCount = 0;
    this._failureMessage = message;
    return await this.addArticleByNextProjectData();
  }

  private isReplaceOperationAvailable(): boolean {
    return !this._baseArticleProjectProcessor ? false : this._baseArticleProjectProcessor.isReplaceOperationAvailable();
  }

  private isMultipleInsertAvailable(): boolean {
    return !this._baseArticleProjectProcessor ? false : this._baseArticleProjectProcessor.isMultipleInsertAvailable();
  }

  private async addArticleByNextProjectData(): Promise<boolean> {
    if (!this._baseArticleProjectProcessor) {
      return false;
    }
    if (this._processingProjectData) {
      this._projectDatas.splice(this._projectDatas.indexOf(this._processingProjectData), 1);
    }
    if (this._projectDatas.length > 0) {
      this._processingArticleCount = this._articleCount;
      this._processingProjectData = this._projectDatas[0];
      if (this._processingProjectData instanceof RangeOnLineArticleData) {
        if (!ArticleUtils.canAddArticleToFreeMounting(this.projectModel.currentProject, this.userModel.user, this._selectedArticle)) {
          await this.addArticleByNextProjectData();
          await this.proServeProjectImportProcessor.articleNOTProjected();
          this.sharedComponentsPresenter.presentOpenInNewWindowComponent(this._selectedArticle);
          return;
        }
      }
      try {
        const data = await this._baseArticleProjectProcessor.addArticleByProjectData(this._processingProjectData, this._selectedArticle);
        await this.addArticleProcess_success(data);
        return true;
      } catch (error) {
        if (error instanceof AddArticleError) {
          Log.warn(error);
          await this.addArticleProcess_failure(error.message);
          return false;
        }
        throw error;
      }
    } else {
      return await this.completeAddArticleOperation();
    }
  }

  private async addArticleByNeighbor(neighborUsageId: string): Promise<void> {
    try {
      const data = await this._baseArticleProjectProcessor.addArticleByNeighbor(neighborUsageId, this._selectedArticle);
      await this.addArticleProcess_success(data);
    } catch (error) {
      if (error instanceof AddArticleError) {
        Log.warn(error);
        await this.addArticleProcess_failure(error.message);
      } else {
        throw error;
      }
    }
  }

  private async completeAddArticleOperation(): Promise<boolean> {
    let success = true;
    if (!this._projectDataCompletelyProcessed && this._failureMessage) {
      this.addArticle_failure(this._failureMessage);
      success = false;
    }
    const operationObservable = await this.pushInsertArticlesOperation();
    await lastValueFrom(operationObservable);
    this.postProcessCreatedArticles();
    this.clear();
    return success;
  }

  private postProcessCreatedArticles(): void {
    const createdContainers: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(this._insertedArticleGUIDs);
    this.openLabelEditorHelper.showLabelEditorForCoverLabelArticles(createdContainers);
  }

  private async pushInsertArticlesOperation(): Promise<Observable<void>> {
    if (this._insertedArticleGUIDs.length === 0) {
      return of(null);
    }
    const attachOperation: UndoAttachArticlesOperation = this.undoRedoUtils.createUndoAttachOperationByUsageIds(this._insertedArticleGUIDs);
    if (!attachOperation) {
      return of(null);
    }
    let insertArticlesOperation: BaseAdvancedOperation;
    const detachOperation: UndoDetachArticlesOperation = this.undoRedoUtils.createUndoDetachOperationByUsageIds(this._detachedArticleGUIDs);
    if (!detachOperation) {
      insertArticlesOperation = new UndoInsertArticlesByLocationOperation(attachOperation, this._moveArticlesMap, this.insertArticleUtils);
    } else {
      await this.detachArticleUsageService.detachArticleUsageFromProjectByIds(this._detachedArticleGUIDs);
      insertArticlesOperation = new UndoReplaceArticlesByLocationOperation(
        attachOperation,
        detachOperation,
        this._moveArticlesMap,
        this.insertArticleUtils
      );
    }
    insertArticlesOperation.additionalOperations = this._createdUndoOperations;
    return await this.undoRedoHelper.pushUndoOperationObservable(insertArticlesOperation);
  }

  private mergeMoveArticlesMap(moveMap: Dictionary<string, Vector3>): void {
    if (!moveMap) {
      return;
    }
    if (!this._moveArticlesMap) {
      this._moveArticlesMap = new Dictionary<string, Vector3>();
    }
    InsertArticleUtils.mergeMoveMap(this._moveArticlesMap, moveMap);
  }

  private concatCreatedUndoOperations(undoOperations: IOperation[]): void {
    if (!undoOperations) {
      return;
    }
    if (!this._createdUndoOperations) {
      this._createdUndoOperations = [];
    }
    this._createdUndoOperations = this._createdUndoOperations.concat(undoOperations);
  }

  private clear(): void {
    this._executing = false;
    this._processingProjectData = null;
    this._projectDatas = [];
    this._detachedArticleGUIDs = [];
    this._insertedArticleGUIDs = [];
    this._moveArticlesMap = null;
    this._createdUndoOperations = null;
    this._articleCount = 0;
    this._processingArticleCount = 0;
    this._failureMessage = null;
    this._projectDataCompletelyProcessed = false;
  }

  private addArticle_failure(data: string | Error): void {
    if (!this._addArticleAlertActive && data) {
      this._addArticleAlertActive = true;
      const message: string = typeof data == 'string' ? data : data.message;
      this.alertHelper.showError('global.title.error', message).then(() => {
        this._addArticleAlertActive = false;
      });
    }
  }

  private subscribeDeselected() {
    this.selectionModel.selectionChanged$.pipe(
      filter(data => data.some(sd => sd.selectionType === "article"))
    ).subscribe(() => this.articleSelectionChanged());
  }
}
